import { createAsyncThunk } from "@reduxjs/toolkit";
import { wrapRequest } from "common/utils";
import { BaseApiResponse, LookupItem, ScenarioGroup } from "common/types";
import { resourceAdequacyService as service, taskSvc, lookupSvc, scenarioGroupService } from "services/serviceStorage";
import { scenarioTypes, resourceAdequacyMode } from 'common/enums';
import {
    RunConfig,
    ScenarioGroupLinks,
    CreateRunConfigPayload,
    RunConfigForCopy,
    RunConfigForEdit
} from "./types";

export const fetchRunConfigs = createAsyncThunk<RunConfig[], number>(
    'resourceAdequacy/fetchRunConfigs',
    async (taskId) => {
        const { data } = await wrapRequest<BaseApiResponse<RunConfig[]>>({
            handler: () => service.getRunConfigs(taskId),
            showNotifications: true
        });

        return data?.result || [];
    }
);

export const fetchPartitioningModes = createAsyncThunk<LookupItem[], void>(
    'resourceAdequacy/fetchPartitioningModes',
    async () => {
        const { data } = await wrapRequest<BaseApiResponse<LookupItem[]>>({
            handler: lookupSvc.getPartitioningModes,
            showNotifications: true
        });

        return data?.result || [];
    }
);

export const fetchScenarios = createAsyncThunk<LookupItem[], { taskId: number, raMode: number }>(
    'resourceAdequacy/fetchScenarios',
    async ({ taskId, raMode }) => {
        const { data } = await wrapRequest<BaseApiResponse<LookupItem[]>>({
            handler: () => taskSvc.getScenariosLookup(taskId, raMode === resourceAdequacyMode.standard ? String(scenarioTypes.nonRA) : undefined),
            showNotifications: true
        });

        return data?.result || [];
    }
);

export const fetchGroups = createAsyncThunk<ScenarioGroup[], { filter: { taskIds: number[] } }>(
    'resourceAdequacy/fetchGroups',
    async ({ filter }) => {
        const { data } = await wrapRequest<BaseApiResponse<{ items: ScenarioGroup[] }>>({
            handler: () => scenarioGroupService.getGroups(filter),
            showNotifications: true
        });

        return data?.result?.items || [];
    }
);

export const deleteRunConfig = createAsyncThunk<void, number>(
    'resourceAdequacy/deleteRunConfig',
    async (runConfigId, { rejectWithValue }) => {
        const err = await wrapRequest<BaseApiResponse<void>>({
            handler: () => service.deleteRunConfig(runConfigId),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);

export const stopConfig = createAsyncThunk<void, number>(
    'resourceAdequacy/stopConfig',
    async (runConfigId, { rejectWithValue }) => {
        const err = await wrapRequest<BaseApiResponse<void>>({
            handler: () => service.stopConfig(runConfigId),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);

export const createFullRunConfig = createAsyncThunk<void, CreateRunConfigPayload>(
    'resourceAdequacy/createFullRunConfig',
    async (runConfig, { rejectWithValue }) => {
        const err = await wrapRequest<BaseApiResponse<void>>({
            handler: () => service.createFullRunConfig(runConfig),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);

export const createRunConfig2Stage = createAsyncThunk<void, CreateRunConfigPayload>(
    'resourceAdequacy/createRunConfig2Stage',
    async (runConfig, { rejectWithValue }) => {
        const err = await wrapRequest<BaseApiResponse<void>>({
            handler: () => service.createRunConfig2Stage(runConfig),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);

export const fetchRunConfigForCopy = createAsyncThunk<RunConfigForCopy, number>(
    'resourceAdequacy/fetchRunConfigForCopy',
    async (runConfigId) => {
        const { data } = await wrapRequest<BaseApiResponse<RunConfigForCopy>>({
            handler: () => service.getRunConfigForCopy(runConfigId),
            showNotifications: true
        });

        return data!.result;
    }
);

export const fetchRunConfig = createAsyncThunk<RunConfigForEdit, number>(
    'resourceAdequacy/fetchRunConfig',
    async (runConfigId) => {
        const { data } = await wrapRequest<BaseApiResponse<RunConfigForEdit>>({
            handler: () => service.getRunConfig(runConfigId),
            showNotifications: true
        });

        return data!.result;
    }
);

export const editRunConfig = createAsyncThunk<void, RunConfigForEdit>(
    'resourceAdequacy/editRunConfig',
    async (runConfig, { rejectWithValue }) => {
        const err = await wrapRequest<BaseApiResponse<void>>({
            handler: () => service.editRunConfig(runConfig),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);

export const runFilteringStage = createAsyncThunk<void, number>(
    'resourceAdequacy/runFilteringStage',
    async (runConfigId, { rejectWithValue }) => {
        const err = await wrapRequest<BaseApiResponse<void>>({
            handler: () => service.runFilteringStage(runConfigId),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);

export const run2StageAfterFiltering = createAsyncThunk<void, number>(
    'resourceAdequacy/run2StageAfterFiltering',
    async (runConfigId, { rejectWithValue }) => {
        const err = await wrapRequest<BaseApiResponse<void>>({
            handler: () => service.run2StageAfterFiltering(runConfigId),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);

export const run2StageOnly = createAsyncThunk<void, number>(
    'resourceAdequacy/run2StageOnly',
    async (runConfigId, { rejectWithValue }) => {
        const err = await wrapRequest<BaseApiResponse<void>>({
            handler: () => service.run2StageOnly(runConfigId),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);